import { buildProxiedAssetUrl } from "@/modules/asset-proxy";
import { AssetAccessTokenState } from "@/store/auth/AssetAccessTokenState";

export interface FileAttachmentHtmlParams {
  /** Unique identifier for the file */
  fileId: string;
  /** Original filename */
  filename: string;
  /** MIME type */
  mimeType: string;
  /** Size in bytes */
  sizeBytes: number;
  /** Raw upload URL (will be proxied if token available) */
  uploadUrl: string;
  /** Display text for the link */
  displayText: string;
  /** Token state for proxying the URL (optional - falls back to raw URL if unavailable) */
  assetAccessTokenState?: AssetAccessTokenState | null;
}

/**
 * Builds an HTML anchor element for a file attachment.
 * The HTML is structured to be parsed into a TipTap File node (download chip UI).
 *
 * The URL is proxied if an asset access token is available, which ensures
 * downloads work across all platforms including iOS.
 */
export function buildFileAttachmentHtml({
  fileId,
  filename,
  mimeType,
  sizeBytes,
  uploadUrl,
  displayText,
  assetAccessTokenState,
}: FileAttachmentHtmlParams): string {
  const proxiedUrl = buildProxiedAssetUrl({
    originalUrl: uploadUrl,
    assetAccessTokenState,
  });
  const downloadUrl = proxiedUrl ?? uploadUrl;

  const escapedFilename = filename.replace(/"/g, "&quot;");
  const escapedDisplayText = displayText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return `<a href="${downloadUrl}" data-type="file" data-file-id="${fileId}" data-file-name="${escapedFilename}" data-file-size-bytes="${sizeBytes}" data-file-mime-type="${mimeType}">${escapedDisplayText}</a>`;
}
